import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Form, { ButtonItem, SimpleItem, EmptyItem, GroupItem, ColCountByScreen, Label } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import { useSelector } from 'react-redux'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import { PatternRule } from 'devextreme-react/data-grid'
import { IconButton, Stack, useMediaQuery, Alert, Box } from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const DESIRED_IMAGE_WIDTH = 200
const DESIRED_IMAGE_HEIGHT = 100

const AddInfoBarForm = (props) => {
    const navigate = useNavigate()
    const { loading } = useSelector((state) => state.infobars)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () =>
            navigate('/settings/configuration/info-bar', {
                state: { navigateFrom: 'cancel-add-info-bar' },
            }),
    }

    const validationRules = {
        dateStart: [{ type: 'required', message: 'Date Start is required.' }],
        dateEnd: [{ type: 'required', message: 'Date End is required.' }],
        sortOrder: [{ type: 'required', message: 'Sort order is required.' }],
        file: [
            { type: 'required', message: 'Image is required.' },
            {
                type: 'custom',
                validationCallback: (e) => e.value[0]?.size <= 256 * 1024,
                message: 'Image should not be more than 256 KB.',
            },
        ],

        heading: [{ type: 'required', message: 'Heading is required.' }],
        subText: [{ type: 'required', message: 'Sub-Text is required.' }],
        timeText: [{ type: 'required', message: 'Time Text is required.' }],
    }

    const navigationLinkEditorOptions = {
        valueChangeEvent: 'keyup',
        maskRules: {
            X: /\d/,
        },
    }

    const navigationLinkPattern = /^(?=.{16,})https:\/\/.*$/

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/settings/configuration/info-bar`, {
                                    state: { navigateFrom: 'cancel-add-info-bar' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Add Info Bar
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form action='' onSubmit={props.addInfoBar}>
                    <Form
                        mode='form'
                        formData={props.InfoBarData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem caption='New Info Bar' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem dataField='heading' validationRules={validationRules.heading}>
                                    <Label text='Heading' />
                                </SimpleItem>
                                <SimpleItem dataField='subText' validationRules={validationRules.subText}>
                                    <Label text='Sub Text' />
                                </SimpleItem>
                                <SimpleItem dataField='timeText' validationRules={validationRules.timeText}>
                                    <Label text='Time Text' />
                                </SimpleItem>
                                <SimpleItem dataField='orderBy' validationRules={validationRules.sortOrder}>
                                    <Label text='Sort Order' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='dateStart'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateStart}
                                    editorOptions={{
                                        displayFormat: 'shortDateShortTime',
                                        type: 'datetime',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem
                                    dataField='dateEnd'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateEnd}
                                    editorOptions={{
                                        displayFormat: 'shortDateShortTime',
                                        type: 'datetime',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem dataField='navigationLink' editorOptions={navigationLinkEditorOptions}>
                                    <Label text='Navigation Link' />
                                    <PatternRule
                                        message='navigation link must start with https:// and be at least 8 characters'
                                        pattern={navigationLinkPattern}
                                    />
                                </SimpleItem>
                                <SimpleItem>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 1 }}>
                                        <Alert
                                            severity='info'
                                            icon={<InfoOutlinedIcon fontSize='small' />}
                                            sx={{
                                                py: 0.5,
                                                maxWidth: 280,
                                                '& .MuiAlert-message': { fontSize: '0.8125rem' },
                                            }}>
                                            Recommended image aspect ratio: {DESIRED_IMAGE_WIDTH} × {DESIRED_IMAGE_HEIGHT} (width × height). Max file size 256 KB.
                                        </Alert>
                                    </Box>
                                </SimpleItem>
                                <SimpleItem
                                    dataField='file'
                                    editorType='dxFileUploader'
                                    validationRules={validationRules.file}
                                    editorOptions={{
                                        uploadMode: 'useForm',
                                        multiple: 'false',
                                        selectButtonText: 'Select file',
                                        labelText: '',
                                        accept: 'image/*',
                                        allowedFileExtensions: ['.jpg', '.jpeg', '.png'],
                                        maxFileSize: 1024 * 512,
                                    }}>
                                    <Label text={`Select an Image (${DESIRED_IMAGE_WIDTH} × ${DESIRED_IMAGE_HEIGHT} px)`} />
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default AddInfoBarForm
